import { Check, X } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Header } from '@/components/Header';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

const getPlans = (t: any) => ({
  free: {
    name: t.free,
    price: '0€',
    period: t.free.toLowerCase(),
    priceId: null,
    popular: false,
    features: [
      { text: `3 ${t.searchesPerDay}`, included: true },
      { text: t.maxSearchesPerMonth.replace('{count}', '15'), included: true },
      { text: `1 ${t.savedResultsPerDay}`, included: true },
      { text: t.aiReformulatorDaily, included: true },
      { text: t.citationExportMonthly.replace('{count}', '2'), included: true },
      { text: t.sourceFilters, included: false },
      { text: t.studyTypes, included: true },
      { text: t.darkMode, included: true },
      { text: t.prioritySupport, included: false },
    ],
  },
  plus: {
    name: 'Plus',
    price: '10€',
    period: t.perMonth,
    priceId: 'price_1SJLZ8Qq5bdadCRVFW9KbUSR',
    popular: true,
    features: [
      { text: `5 ${t.searchesPerDay}`, included: true },
      { text: t.maxSearchesPerMonth.replace('{count}', '60'), included: true },
      { text: t.savedResultsPermanent.replace('{count}', '3'), included: true },
      { text: t.aiReformulatorUnlimited, included: true },
      { text: t.citationExportUnlimited, included: true },
      { text: t.sourceFilters, included: true },
      { text: t.studyTypes, included: true },
      { text: t.darkMode, included: true },
      { text: t.prioritySupport, included: false },
    ],
  },
  pro: {
    name: 'Pro',
    price: '20€',
    period: t.perMonth,
    priceId: 'price_1SJLZhQq5bdadCRVOEM15XhS',
    popular: false,
    features: [
      { text: t.unlimitedSearches, included: true },
      { text: t.noMonthlyLimit, included: true },
      { text: t.unlimitedSavedResults, included: true },
      { text: t.aiReformulatorUnlimited, included: true },
      { text: t.citationExportUnlimited, included: true },
      { text: t.sourceFilters, included: true },
      { text: t.studyTypes, included: true },
      { text: t.darkMode, included: true },
      { text: t.prioritySupportEarlyAccess, included: true },
    ],
  },
});

const Pricing = () => {
  const { plan, user, loading } = useSubscription();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  
  const PLANS = getPlans(t);

  const handleSubscribe = async (priceId: string | null) => {
    if (!user) {
      toast({
        title: t.loginRequired,
        description: t.loginRequiredDescription,
        variant: 'destructive',
      });
      navigate('/auth');
      return;
    }

    if (!priceId) {
      toast({
        title: t.free,
        description: t.currentPlan,
      });
      return;
    }

    // Show informative message if language is Catalan
    if (language === 'ca') {
      toast({
        title: t.checkoutInSpanish,
        description: '',
      });
    }

    // Open window immediately to avoid mobile popup blockers
    const checkoutWindow = window.open('', '_blank');
    
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { priceId, locale: language },
      });

      if (error) throw error;
      
      // Check for active subscription error
      if (data?.error === 'ACTIVE_SUBSCRIPTION_EXISTS') {
        if (checkoutWindow) {
          checkoutWindow.close();
        }
        toast({
          title: t.activeSubscriptionError,
          description: t.activeSubscriptionErrorDescription,
          variant: 'destructive',
        });
        return;
      }

      if (data?.url && checkoutWindow) {
        checkoutWindow.location.href = data.url;
      } else if (data?.url) {
        // Fallback if window didn't open
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      if (checkoutWindow) {
        checkoutWindow.close();
      }
      toast({
        title: t.saveError,
        description: t.saveErrorDescription,
        variant: 'destructive',
      });
    }
  };

  const handleManageSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast({
        title: t.saveError,
        description: t.saveErrorDescription,
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Preus i Plans IA Mèdica | Articles Mèdics i Cites Automàtiques - MedAI Cite</title>
        <meta name="description" content="Plans de IA mèdica per cercar articles mèdics i generar cites automàtiques. Compara Free, Plus i Pro per a investigació mèdica professional amb accés a PubMed." />
        <link rel="canonical" href="https://medai-cite.com/pricing" />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://medai-cite.com/pricing" />
        <meta property="og:title" content="Preus i Plans IA Mèdica | MedAI Cite" />
        <meta property="og:description" content="Plans de IA mèdica per cercar articles mèdics i generar cites automàtiques. Compara Free, Plus i Pro per a investigació mèdica." />
        <meta property="og:image" content="https://medai-cite.com/medai-cite-logo-og.png" />
        <meta property="og:site_name" content="MedAI Cite" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://medai-cite.com/pricing" />
        <meta name="twitter:title" content="Preus Plans IA Mèdica | MedAI Cite" />
        <meta name="twitter:description" content="Plans de IA mèdica per articles mèdics i cites automàtiques d'investigació mèdica." />
        <meta name="twitter:image" content="https://medai-cite.com/medai-cite-logo-og.png" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Header />
      
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold mb-3 bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">{t.choosePlan}</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.findPerfectPlan}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto mb-8">
          {Object.entries(PLANS).map(([key, planDetails]) => {
            const isCurrentPlan = plan === key;
            const isPro = key === 'pro';
            const isFree = key === 'free';
            const isPlus = key === 'plus';
            
            return (
              <Card
                key={key}
                className={`relative p-6 transition-all duration-300 hover:shadow-xl ${
                  isPro
                    ? 'bg-gradient-to-br from-[#0057FF] to-[#0041BB] text-white border-[#0057FF] shadow-2xl scale-[1.02] md:scale-105'
                    : isFree
                      ? 'bg-gradient-to-br from-slate-50 to-blue-50/30 dark:from-slate-900/50 dark:to-blue-950/30 border-slate-200 dark:border-slate-800'
                      : isPlus
                        ? 'bg-gradient-to-br from-white to-blue-50/20 dark:from-slate-900 dark:to-blue-950/20 border-primary/20 shadow-lg'
                        : 'border-border bg-card'
                } ${isCurrentPlan ? 'ring-2 ring-primary' : ''}`}
              >
                {planDetails.popular && !isPro && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-1 rounded-full text-xs font-medium">
                    {t.mostPopular}
                  </div>
                )}
                
                {isCurrentPlan && (
                  <div className="absolute -top-3 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                    {t.yourPlan}
                  </div>
                )}

                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    {isFree && <span className="text-2xl">🩺</span>}
                    {isPlus && <span className="text-2xl">⚕️</span>}
                    {isPro && <span className="text-2xl">💎</span>}
                    <h3 className={`text-xl font-bold ${isPro ? 'text-white' : ''}`}>
                      {planDetails.name}
                    </h3>
                  </div>
                  
                  <p className={`text-sm mb-3 leading-relaxed ${
                    isPro ? 'text-white/90' : 'text-muted-foreground'
                  }`}>
                    {isFree && t.planBasicSubtitle}
                    {isPlus && t.planPlusSubtitle}
                    {isPro && t.planProSubtitle}
                  </p>
                  
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className={`text-4xl font-bold ${isPro ? 'text-white' : 'text-foreground'}`}>
                      {planDetails.price}
                    </span>
                    <span className={isPro ? 'text-white/70' : 'text-muted-foreground'}>
                      {planDetails.period}
                    </span>
                  </div>
                  
                  <p className={`text-xs italic ${
                    isPro ? 'text-white/70' : 'text-muted-foreground'
                  }`}>
                    {isFree && t.planBasicTrust}
                    {isPlus && t.planPlusTrust}
                    {isPro && t.planProTrust}
                  </p>
                </div>

                <div className={`mb-4 p-3 rounded-lg ${
                  isPro 
                    ? 'bg-white/10 border border-white/20' 
                    : 'bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200/50 dark:border-blue-800/50'
                }`}>
                  <p className={`text-sm font-medium mb-1.5 ${isPro ? 'text-white' : 'text-foreground'}`}>
                    {isFree && t.planBasicDescription}
                    {isPlus && t.planPlusDescription}
                    {isPro && t.planProDescription}
                  </p>
                  <p className={`text-xs flex items-center gap-1 ${
                    isPro ? 'text-white/90' : 'text-primary'
                  }`}>
                    <span>🔹</span>
                    {isFree && t.planBasicHighlight}
                    {isPlus && t.planPlusHighlight}
                    {isPro && t.planProHighlight}
                  </p>
                </div>

                <ul className="space-y-2 mb-6">
                  {planDetails.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      {feature.included ? (
                        <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                          isPro ? 'text-green-300' : 'text-green-600 dark:text-green-500'
                        }`} />
                      ) : (
                        <X className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                          isPro ? 'text-white/30' : 'text-muted-foreground/40'
                        }`} />
                      )}
                      <span className={`text-sm ${
                        isPro 
                          ? (feature.included ? 'text-white font-medium' : 'text-white/40')
                          : (feature.included ? 'text-foreground' : 'text-muted-foreground/60')
                      }`}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full font-bold transition-all duration-300 ${
                    isPro 
                      ? 'bg-white text-[#0057FF] hover:bg-slate-100 hover:scale-105 shadow-lg'
                      : isPlus
                        ? 'bg-primary hover:bg-primary/90 hover:scale-105'
                        : 'hover:scale-105'
                  }`}
                  variant={isPro || isPlus ? 'default' : 'outline'}
                  onClick={() => handleSubscribe(planDetails.priceId)}
                  disabled={loading || isCurrentPlan}
                >
                  {isCurrentPlan 
                    ? t.currentPlan
                    : user 
                      ? `${t.upgradeToPlan} ${planDetails.name}` 
                      : `${t.viewPlan} ${planDetails.name}`}
                </Button>
              </Card>
            );
          })}
        </div>

        {user && plan !== 'free' && (
          <div className="text-center">
            <Button variant="outline" onClick={handleManageSubscription}>
              {t.manageSubscription}
            </Button>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default Pricing;
